/* ---------------------------------------------------------------------
   gc-engrave-spots: where the initials go on the new line (client decisions, 3 Nov 2026).
   Config: snippets/gc-engrave-config.liquid (script[data-gc-engrave]), from the product's
   gcx.engrave_spots. Only on a product page whose product has that metafield; the cases that
   existed before keep the drawer exactly as it was.

   1. The drawer's picture. personalize-drawer.js shows a color's main photo. Where the initials
      go on another side (a card holder's back, a wallet opened flat), that color's preview photo
      replaces it, matched by file name, at the size the drawer asked for.
   2. The initials on that picture. A measured photo gets is-gc-anchored and --gcx-x/y/w/rot on
      [data-image-container]: gc-personalize-drawer.css then sets the letters where they are
      stamped, sized like the gallery spot (assets/gcpx-pdpspot.css). A photo with no measured
      spot (its preview photo is still waiting for approval) gets is-gc-unplaced: the letters sit
      under the picture instead of on a side they will not be stamped on.
   3. Remove Logo (ID badge holders). While the add-on is ticked (window.GCLogo, owned by
      assets/gc-logo-addon.js) the initials move to the logo's place, in the drawer and on the
      gallery's spots (data-gcx-nl, snippets/gcpx-pdpspot.liquid), and the placement sentence says so.
   4. The words. The drawer says where this product is stamped: under the initials field and in
      "About Personalization" (personalize.placement_<type>, translated).
   5. Color-only products. The drawer's "Step 1 Select Model" header has no model to offer on a
      wallet; it is hidden and the steps are numbered from the color.
   The minified drawer is not touched: everything here watches what it does.
   --------------------------------------------------------------------- */
(function () {
  if (window.__gcEngrave) return;
  var el = document.querySelector('script[data-gc-engrave]');
  if (!el) return;
  var cfg;
  try { cfg = JSON.parse(el.textContent); } catch (e) { return; }
  window.__gcEngrave = cfg;
  var PID = String(cfg.product);
  var M = cfg.m || {}, V = cfg.v || {}, F = cfg.f || {}, LF = cfg.lf || {};

  function fileOf(url) {
    var f = String(url || '').split(/[?#]/)[0];
    f = f.slice(f.lastIndexOf('/') + 1).replace(/\.(jpe?g|png|webp|avif|gif)$/i, '');
    return f.replace(/_(\d+x\d*|x\d+)(_crop_[a-z]+)?(@\dx)?$/i, '').toLowerCase();
  }
  function abs(url) { return String(url).indexOf('//') === 0 ? 'https:' + url : String(url); }
  function widthOf(src) {
    var m = /[?&]width=(\d+)/.exec(src) || /_(\d+)x\d*(_crop_[a-z]+)?\.[a-z]+(\?|$)/i.exec(src);
    return m ? +m[1] : 600;
  }
  function sized(url, w) {
    var u = abs(url).replace(/([?&])width=\d+&?/, '$1').replace(/[?&]$/, '');
    return u + (u.indexOf('?') < 0 ? '?' : '&') + 'width=' + w;
  }

  /* file name -> what it is: a color's main photo, a preview photo, or a logo-free twin */
  var BY_FILE = Object.create(null);
  Object.keys(F).forEach(function (id) { BY_FILE[fileOf(F[id])] = { main: id }; });
  Object.keys(M).forEach(function (id) { if (M[id] && M[id].u) BY_FILE[fileOf(M[id].u)] = { preview: id }; });
  Object.keys(LF).forEach(function (id) { if (LF[id] && LF[id].u) BY_FILE[fileOf(LF[id].u)] = { twin: id }; });

  function logoRemoved() {
    var G = window.GCLogo;
    try { return !!(G && typeof G.isOn === 'function' && G.isOn()); } catch (e) { return false; }
  }
  function spotOf(previewId) {
    var e = M[previewId] && M[previewId].s;
    if (!e) return null;
    var s = logoRemoved() && e.nl ? e.nl : e;
    var x = parseFloat(s.x), y = parseFloat(s.y), w = parseFloat(s.w), r = parseFloat(s.r) || 0;
    return isFinite(x) && isFinite(y) && w > 0 ? { x: x, y: y, w: w, r: r } : null;
  }
  function drawerApi() { return window.PersonalizeDrawer || null; }
  function ours() {
    var d = drawerApi();
    var cur = d && d.currentProductId;
    return !cur || String(cur) === PID;
  }

  /* ---------------------------------------------------------- 1 + 2: the drawer's picture */
  var drawer = null, img = null, box = null;
  var VARS = ['--gcx-x', '--gcx-y', '--gcx-w', '--gcx-wn', '--gcx-rot'];
  function clear() {
    if (!box) return;
    box.classList.remove('is-gc-anchored', 'is-gc-unplaced');
    VARS.forEach(function (v) { box.style.removeProperty(v); });
  }
  function anchor(s) {
    box.classList.add('is-gc-anchored');
    box.classList.remove('is-gc-unplaced');
    box.style.setProperty('--gcx-x', s.x + '%');
    box.style.setProperty('--gcx-y', s.y + '%');
    box.style.setProperty('--gcx-w', s.w + '%');
    box.style.setProperty('--gcx-wn', String(s.w));
    box.style.setProperty('--gcx-rot', s.r + 'deg');
  }
  function place() {
    if (!img || !box) return;
    var src = img.getAttribute('src') || '';
    if (!src || !ours()) { clear(); return; }
    var hit = BY_FILE[fileOf(src)];
    /* a color's main photo whose initials go on another side: show that side */
    if (hit && hit.main && V[hit.main] && V[hit.main] !== hit.main && M[V[hit.main]] && M[V[hit.main]].u) {
      var want = sized(M[V[hit.main]].u, widthOf(src));
      if (want !== src) { img.setAttribute('src', want); return; } /* the observer comes back here */
    }
    var pv = hit && (hit.preview || (hit.main && V[hit.main]) || (hit.twin && V[hit.twin]));
    var s = pv && spotOf(pv);
    if (s) anchor(s);
    else {
      clear();
      box.classList.add('is-gc-unplaced');
    }
  }

  /* ---------------------------------------------------------- 4: the words */
  function sentence() { return (logoRemoved() && cfg.p_nl) ? cfg.p_nl : (cfg.p || ''); }
  function words() {
    if (!drawer) return;
    var line = drawer.querySelector('[data-gc-placement]');
    if (!line) {
      var hint = drawer.querySelector('.personalize-drawer__hint');
      if (hint && hint.parentNode) {
        line = document.createElement('p');
        line.className = 'personalize-drawer__placement';
        line.setAttribute('data-gc-placement', '');
        hint.parentNode.insertBefore(line, hint.nextSibling);
      }
    }
    var desc = drawer.querySelector('[data-gc-placement-desc]');
    if (desc && !desc.hasAttribute('data-gc-default')) desc.setAttribute('data-gc-default', desc.textContent);
    var on = ours() && !!sentence();
    if (line) {
      if (on && line.textContent !== sentence()) line.textContent = sentence();
      line.hidden = !on;
    }
    if (desc) {
      var t = on ? sentence() : desc.getAttribute('data-gc-default');
      if (desc.textContent !== t) desc.textContent = t;
    }
  }

  /* ---------------------------------------------------------- 5: color-only products */
  function renumber(node, n) {
    if (!node) return;
    var t = node.textContent || '';
    var next = /\d+\s*$/.test(t) ? t.replace(/\d+\s*$/, String(n)) : t;
    if (next !== t) node.textContent = next;
  }
  function steps() {
    if (!drawer || !ours()) return;
    var opts = drawer.querySelector('[data-drawer-variant-options]');
    var content = drawer.querySelector('[data-variant-collapse-content]');
    var head = content && content.querySelector('.personalize-drawer__step-header:not([data-color-step])');
    var color = drawer.querySelector('[data-color-step]');
    var noModel = !!opts && !opts.querySelector('.drawer-variant-option');
    var hasColor = !!color && color.style.display !== 'none';
    var want = noModel ? 'none' : '';
    if (head && head.style.display !== want) head.style.display = want;
    if (opts) opts.classList.toggle('gc-empty', noModel);
    if (noModel && hasColor) {
      renumber(color.querySelector('.personalize-drawer__step-number'), 1);
      renumber(drawer.querySelector('[data-initials-step-number]'), 2);
    } else if (noModel) {
      renumber(drawer.querySelector('[data-initials-step-number]'), 1);
    }
  }

  /* ---------------------------------------------------------- 3: Remove Logo on the gallery's spots */
  function gallery() {
    var off = logoRemoved();
    [].forEach.call(document.querySelectorAll('[data-gcpx-ps][data-gcx-nl]'), function (l) {
      if (!l.hasAttribute('data-gcx-at')) {
        l.setAttribute('data-gcx-at', ['--gcx-x', '--gcx-y', '--gcx-w', '--gcx-rot'].map(function (v) {
          return parseFloat(l.style.getPropertyValue(v)) || 0;
        }).join(','));
      }
      var a = (off ? l.getAttribute('data-gcx-nl') : l.getAttribute('data-gcx-at')).split(',');
      l.style.setProperty('--gcx-x', (+a[0]) + '%');
      l.style.setProperty('--gcx-y', (+a[1]) + '%');
      l.style.setProperty('--gcx-w', (+a[2]) + '%');
      l.style.setProperty('--gcx-rot', (+a[3] || 0) + 'deg');
      l.classList.toggle('is-gc-nologo', off);
    });
  }

  function all() { place(); words(); steps(); }

  var booted = false, waiting = null;
  function boot() {
    if (booted) return;
    drawer = document.querySelector('[data-personalize-drawer]');
    if (!drawer) {
      /* the drawer is built from its <template> once the page is parsed: wait for it */
      if (!waiting && typeof MutationObserver === 'function') {
        waiting = new MutationObserver(function () {
          if (document.querySelector('[data-personalize-drawer]')) { waiting.disconnect(); boot(); }
        });
        waiting.observe(document.body, { childList: true });
      }
      return;
    }
    booted = true;
    if (waiting) waiting.disconnect();
    img = drawer.querySelector('[data-personalize-image]');
    box = drawer.querySelector('[data-image-container]');
    if (typeof MutationObserver === 'function') {
      if (img) new MutationObserver(place).observe(img, { attributes: true, attributeFilter: ['src'] });
      /* opening (class "open") and every rebuild of the steps */
      new MutationObserver(function () { if (drawer.classList.contains('open')) all(); })
        .observe(drawer, { attributes: true, attributeFilter: ['class'] });
      var sel = drawer.querySelector('[data-drawer-variant-selector]');
      if (sel) new MutationObserver(steps).observe(sel, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['style'] });
      var ini = drawer.querySelector('[data-initials-step-number]');
      if (ini) new MutationObserver(steps).observe(ini, { childList: true, characterData: true, subtree: true });
    }
    var G = window.GCLogo;
    if (G && typeof G.onChange === 'function') G.onChange(function () { all(); gallery(); });
    else document.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('[data-gc-logo-addon]')) setTimeout(function () { all(); gallery(); }, 0);
    }, true);
    all();
    gallery();
  }
  /* deferred scripts run before DOMContentLoaded, and GCLogo (gc-logo-spot.js) comes after this
     file in the page: wait for the lot */
  if (document.readyState === 'complete') boot();
  else {
    document.addEventListener('DOMContentLoaded', boot);
    window.addEventListener('load', boot);
  }
})();
